import { z } from 'zod';

import { PatientModel } from 'models';

import { hasText } from 'core/utils';

/**
 * Create a dynamic patient schema based on validation rules
 * @param patientIdRequired Whether patient ID is required
 * @returns Zod schema for patient validation
 */
export const createPatientSchema = (patientIdRequired: boolean) => {
  // Base schema for required fields
  return z
    .object({
      firstName: z.string().min(1, { message: 'First name is required' }),
      lastName: z.string().min(1, { message: 'Last name is required' }),
      patientNumber: patientIdRequired ? z.string().min(1, { message: 'Patient ID is required' }) : z.string().optional(),
      // Optional fields with explicit types
      //dateOfBirth: z.string().optional(),
      //gender: z.string().optional(),
      // All other fields are handled by the .passthrough()
    })
    .passthrough(); // Allow other fields without validation
};

/**
 * Validates patient data and returns validation results
 * @param patient The patient to validate
 * @param patientIdRequired Whether patient ID is required
 * @returns Object with isValid status and any validation errors
 */
export const validatePatientWithZod = (patient: PatientModel | null, patientIdRequired: boolean) => {
  if (!patient) {
    return { isValid: false, errors: { _form: 'Patient data is missing' } };
  }

  const schema = createPatientSchema(patientIdRequired);
  const result = schema.safeParse(patient);

  if (result.success) {
    return { isValid: true, errors: {} };
  } else {
    // Convert Zod errors to a more usable format
    const formattedErrors: Record<string, string> = {};
    result.error.errors.forEach((err) => {
      const path = err.path.join('.');
      formattedErrors[path] = err.message;
    });
    return { isValid: false, errors: formattedErrors };
  }
};

/**
 * Legacy validation function (maintained for backward compatibility)
 * @param patient The patient to validate
 * @param patientIdRequired Whether patient ID is required
 * @returns true if patient is valid, false otherwise
 */
export const validatePatient = (patient: PatientModel | null, patientIdRequired: boolean): boolean => {
  if (!patient) return false;

  // Check required fields
  if (!hasText(patient.firstName) || !hasText(patient.lastName)) {
    return false;
  }

  // Check patientNumber if required
  if (patientIdRequired && !hasText(patient.patientNumber)) {
    return false;
  }

  return true;
};
